import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://rpm.healthstreamanalytics.com';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const login = async (email: string, password: string) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const register = async (userData: any) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

// Patients
export const getPatients = async () => {
  const response = await api.get('/patients');
  return response.data;
};

export const getPatient = async (id: string) => {
  const response = await api.get(`/patients/${id}`);
  return response.data;
};

export const createPatient = async (patientData: {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  room_number: string;
  facility_id?: string;
}) => {
  const response = await api.post('/patients', patientData);
  return response.data;
};

export const transferPatient = async (patientId: string, facilityId: string) => {
  const response = await api.patch(`/patients/${patientId}/facility`, { facility_id: facilityId });
  return response.data;
};

export const getPatientVitals = async (id: string, hours = 24) => {
  const response = await api.get(`/vitals/patient/${id}?hours=${hours}`);
  return response.data;
};

export const getPatientLatestVitals = async (id: string) => {
  const response = await api.get(`/patients/${id}/vitals/latest`);
  return response.data;
};

// Alerts
export const getAlerts = async (status = 'active') => {
  const response = await api.get(`/alerts?status=${status}`);
  return response.data;
};

export const acknowledgeAlert = async (id: string) => {
  const response = await api.patch(`/alerts/${id}/acknowledge`);
  return response.data;
};

export const resolveAlert = async (id: string) => {
  const response = await api.patch(`/alerts/${id}/resolve`);
  return response.data;
};

// Facilities
export const getFacilities = async () => {
  const response = await api.get('/facilities');
  return response.data;
};

// Devices
export const getDevices = async (facilityId?: string) => {
  const url = facilityId ? `/devices?facilityId=${facilityId}` : '/devices';
  const response = await api.get(url);
  return response.data;
};

export const assignDevice = async (deviceId: string, patientId: string) => {
  const response = await api.put(`/devices/${deviceId}/assign`, { patient_id: patientId });
  return response.data;
};

export const uncoupleDevice = async (deviceId: string) => {
  const response = await api.put(`/devices/${deviceId}/uncouple`);
  return response.data;
};

export const createDevice = async (deviceData: { device_type: string; serial_number: string; facility_id?: string }) => {
  const response = await api.post('/devices', deviceData);
  return response.data;
};

export const getPatientDevices = async (patientId: string) => {
  const response = await api.get(`/devices?patientId=${patientId}`);
  return response.data;
};

// Health Index (Visensia)
export const getPatientHealthIndex = async (patientId: string) => {
  const response = await api.get(`/health-index/patient/${patientId}`);
  return response.data;
};

export const getPatientHealthIndexHistory = async (patientId: string, hours = 24) => {
  const response = await api.get(`/health-index/patient/${patientId}/history?hours=${hours}`);
  return response.data;
};

export const calculatePatientHealthIndex = async (patientId: string) => {
  const response = await api.post(`/health-index/patient/${patientId}/calculate`);
  return response.data;
};

export const getHealthIndexDashboard = async () => {
  const response = await api.get('/health-index/dashboard');
  return response.data;
};

// Integrations - Oura Ring
export const getOuraAuthUrl = async (deviceId: string) => {
  const response = await api.get(`/integrations/oura/connect/${deviceId}`);
  return response.data;
};

export const disconnectOuraDevice = async (deviceId: string) => {
  const response = await api.post(`/integrations/oura/disconnect/${deviceId}`);
  return response.data;
};

export const syncOuraDevice = async (deviceId: string) => {
  const response = await api.post(`/integrations/oura/sync/${deviceId}`);
  return response.data;
};

// Integrations - Apple Health
export const getAppleHealthApiKey = async (deviceId: string) => {
  const response = await api.get(`/enrollment/api-key/${deviceId}`);
  return response.data;
};

export const getAppleHealthStatus = async (deviceId: string) => {
  const response = await api.get(`/integrations/apple-health/status/${deviceId}`);
  return response.data;
};

// Enrollment
export const generateEnrollmentLink = async (data: {
  patientId: string;
  deviceType: string;
  sendEmail?: boolean;
  email?: string;
  sendSMS?: boolean;
  phone?: string;
}) => {
  const response = await api.post('/enrollment/generate', data);
  return response.data;
};

export const getEnrollmentStatus = async (patientId: string) => {
  const response = await api.get(`/enrollment/status/${patientId}`);
  return response.data;
};

export const resendEnrollmentInvitation = async (invitationId: string, data: { email?: string; phone?: string }) => {
  const response = await api.post(`/enrollment/resend/${invitationId}`, data);
  return response.data;
};

export const deleteEnrollmentInvitation = async (invitationId: string) => {
  const response = await api.delete(`/enrollment/${invitationId}`);
  return response.data;
};

// Providers
export const getProviders = async () => {
  const response = await api.get('/providers');
  return response.data;
};

export const createProvider = async (data: {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  provider_type?: string;
  specialization?: string;
  notification_preference?: string;
}) => {
  const response = await api.post('/providers', data);
  return response.data;
};

export const updateProvider = async (id: string, data: {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  provider_type?: string;
  specialization?: string;
  notification_preference?: string;
  active?: boolean;
}) => {
  const response = await api.put(`/providers/${id}`, data);
  return response.data;
};

export const deleteProvider = async (id: string) => {
  await api.delete(`/providers/${id}`);
};

// Patient-provider assignments
export const getPatientProviders = async (patientId: string) => {
  const response = await api.get(`/patient/${patientId}/providers`);
  return response.data;
};

export const assignProvider = async (patientId: string, data: {
  provider_id: string;
  is_primary?: boolean;
  assignment_role?: string;
  notes?: string;
}) => {
  const response = await api.post(`/patient/${patientId}/providers`, data);
  return response.data;
};

export const removeProviderAssignment = async (patientId: string, providerId: string) => {
  await api.delete(`/patient/${patientId}/providers/${providerId}`);
};

// Alert settings
export const getAlertSettings = async (patientId: string) => {
  const response = await api.get(`/patient/${patientId}/alert-settings`);
  return response.data;
};

export const updateAlertSettings = async (patientId: string, settings: {
  clustering_window_minutes?: number;
  clustering_threshold?: number;
  clustering_min_severity?: string;
  grouping_logic?: 'OR' | 'AND';
  grouped_vitals?: string[];
  alert_cooldown_minutes?: number;
  clustering_enabled?: boolean;
}) => {
  const response = await api.put(`/patient/${patientId}/alert-settings`, settings);
  return response.data;
};

export const resetAlertSettings = async (patientId: string) => {
  const response = await api.post(`/patient/${patientId}/alert-settings/reset`);
  return response.data;
};

// Sleep Thresholds API
export const getSleepThresholds = async (patientId: string) => {
  const response = await api.get(`/sleep-thresholds/${patientId}`);
  return response.data;
};

export const updateSleepThresholds = async (patientId: string, thresholds: any) => {
  const response = await api.put(`/sleep-thresholds/${patientId}`, thresholds);
  return response.data;
};

// Emfit API
export const getEmfitDevices = async () => {
  const response = await api.get('/emfit/devices');
  return response.data.devices || [];
};

export const getEmfitDevice = async (serialNumber: string) => {
  const response = await api.get(`/emfit/devices/${serialNumber}`);
  return response.data.device;
};

export const registerEmfitDevice = async (data: {
  serial_number: string;
  facility_id: number;
  resident_id: number;
  room_number: string;
}) => {
  const response = await api.post('/emfit/devices', data);
  return response.data.device;
};

export const getEmfitLiveData = async (serialNumber: string, limit = 100) => {
  const response = await api.get(`/emfit/live-data/${serialNumber}?limit=${limit}`);
  return response.data.data || [];
};

export const getEmfitSleepSummaries = async (serialNumber: string, limit = 7) => {
  const response = await api.get(`/emfit/sleep-summaries/${serialNumber}?limit=${limit}`);
  return response.data.summaries || [];
};

export const getPatientSleepSummaries = async (patientId: string, limit = 7) => {
  const response = await api.get(`/emfit/patient/${patientId}/sleep-summaries?limit=${limit}`);
  return response.data.summaries || [];
};

export const getSleepSessions = async (patientId: string, days = 14) => {
  const response = await api.get(`/sleep/patient/${patientId}?days=${days}`);
  return response.data || [];
};

export const getEmfitEvents = async (serialNumber: string, limit = 50) => {
  const response = await api.get(`/emfit/events/${serialNumber}?limit=${limit}`);
  return response.data.events || [];
};

export const getEmfitBackfillStatus = async () => {
  const response = await api.get('/emfit/backfill/status');
  return response.data;
};

export const getEmfitBackfillHistory = async (limit = 20) => {
  const response = await api.get(`/emfit/backfill/history?limit=${limit}`);
  return response.data;
};

export const triggerEmfitBackfill = async (data: {
  serial_number: string;
  start_date?: string;
  end_date?: string;
}) => {
  const response = await api.post('/emfit/backfill/trigger', data);
  return response.data;
};

export default api;
